import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { StripePaymentIntent } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import SetupPaymentIntent from '@src/components/payment/PaymetnIntentForm'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import { irrecoverableErrorCodes } from './stripe'
import StripePaymentForm from './StripePaymentForm'

const DepositFundsButton: React.FC<React.PropsWithChildren<ButtonProps>> = (
  buttonProps,
) => {
  const shouldRender = useFeatureFlag('allow-stripe-deposits')
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const [currentIntent, setCurrentIntent] = useState<StripePaymentIntent>()
  const [step, setStep] = useState(0)
  const [isOpen, setOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const clearAndClose = () => {
    setCurrentIntent(undefined)
    setStep(0)
    setOpen(false)
  }
  return shouldRender ? (
    <>
      <Button
        {...{
          variant: 'contained',
          color: 'secondary',
          ...buttonProps,
        }}
        onClick={(e) => {
          buttonProps.onClick?.(e)
          setOpen(true)
        }}
      >
        {I18n.t('content.payment.addFunds')}
      </Button>
      <Dialog
        open={isOpen}
        onClose={clearAndClose}
        fullWidth={true}
        maxWidth="xs"
        sx={{
          '.MuiPaper-root': {
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle
          align="center"
          sx={(theme) => ({
            position: 'relative',
            overflow: 'visible',
            backgroundColor: theme.palette.grey[100],
            borderRadius: theme.spacing(2, 2, 0, 0),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          })}
        >
          {retailerAccount.name}
          <Typography variant="subtitle2">
            {I18n.t('content.payment.depositFunds')}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Avatar
              sx={{ marginTop: '-1em' }}
              src={retailerAccount.logo_url}
              alt={retailerAccount.name}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {step === 0 ? (
            <SetupPaymentIntent
              amountInCents={Number(currentIntent?.amount || 0)}
              onComplete={(value) => {
                setCurrentIntent(value)
                setStep(1)
              }}
              businessId={retailerAccount.business_id}
            />
          ) : (
            <>
              {currentIntent && (
                <StripePaymentForm
                  updateIntent={setCurrentIntent}
                  paymentIntent={currentIntent}
                  onBack={() => setStep(0)}
                  onComplete={() => {
                    enqueueSnackbar(I18n.t('content.payment.depositSuccess'), {
                      variant: 'success',
                    })
                    clearAndClose()
                  }}
                  onFail={(type: string) => {
                    const message = I18n.t(`content.payment.error.${type}`, {
                      defaultValue: I18n.t('shared.errorSendingRequest'),
                    })

                    if (type === 'polling_timeout') {
                      enqueueSnackbar(message, {
                        variant: 'warning',
                      })
                      clearAndClose()
                    } else {
                      enqueueSnackbar(message, {
                        variant: 'error',
                      })
                    }

                    if (irrecoverableErrorCodes.includes(type)) {
                      clearAndClose()
                      return
                    }
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  ) : null
}
export default DepositFundsButton
